import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { ReservationService } from '@app/reservation/reservation.service';
import { TranslateService } from '@ngx-translate/core';
import { Constants, HOTEL_SYSTEM_CONFIG } from '@app/core/constants';
import { ConfirmModalComponent } from '@app/shared/confirm-modal/confirm-modal.component';
import { PmsProductModalComponent } from './edit-pms-product-modal/pms-product-modal.component';
@Component({
  selector: 'app-pms-integration',
  templateUrl: './pms-integration.component.html',
  styleUrls: ['./pms-integration.component.scss']
})
export class PmsIntegrationComponent implements OnInit, OnDestroy {
  HOTEL_SYSTEM_FIELDS: { [system: string]: any[] } = {};

  // Dropdown options are built from HOTEL_SYSTEM_CONFIG keys.
  hotelSystems = Object.keys(HOTEL_SYSTEM_CONFIG).map(system => ({
    id: system, // e.g. "Oracle", "ASA", etc.
    value: system // Display text is the same.
  }));

  dynamicForm: FormGroup;
  selectedHotelSystem: string = '';
  dropdownSelected: string | null = null;
  dateBasedFilter: any = Constants.pmsIntegrationSettings.filter;
  dateBasedFilterOptions = [
    { value: 'arrival', label: this.translate.instant('HotelReservations.DateArrival') },
    { value: 'stay', label: this.translate.instant('HotelReservations.DateStay') }
  ];
  // durations in minutes
  stayTimes = [
    { label: `1 ${this.translate.instant('HotelReservations.Hour')}`, value: 60 },
    { label: `1.5 ${this.translate.instant('HotelReservations.Hours')}`, value: 90 },
    { label: `2 ${this.translate.instant('HotelReservations.Hours')}`, value: 120 },
    { label: `2.5 ${this.translate.instant('HotelReservations.Hours')}`, value: 150 },
    { label: `4 ${this.translate.instant('HotelReservations.Hours')}`, value: 240 }
  ];

  mealTimeOptions: any = [];

  // models bound to selects
  stayTimeMinutes: number = Constants.pmsIntegrationSettings.stayTime;
  breakfastTime: string = Constants.pmsIntegrationSettings.breakfastTime;
  lunchTime: string = Constants.pmsIntegrationSettings.lunchTime;
  dinnerTime: string = Constants.pmsIntegrationSettings.dinnerTime;

  integrationData: any[] = [];
  productIntegrationData: any[] = [];
  productIntegrationSettingData: any[] = [];
  systemIntegrations: { system: string; data: any[] }[] = [];
  loading: boolean = false;
  clientId: string;
  mealOptions = [
    { value: 'FR', label: this.translate.instant('HotelReservations.Breakfast'), color: 'lightsalmon' },
    { value: 'HP', label: this.translate.instant('HotelReservations.HalfBoard'), color: 'lightpink' },
    { value: 'VP', label: this.translate.instant('HotelReservations.FullBoard'), color: 'lightblue' }
  ];

  mealServerdAt = [
    { label: 'Arrival only', value: 0 },
    { label: 'Departure only', value: 1 },
    { label: 'Both', value: 2 },
    { label: 'No on edges', value: 3 }
  ];

  breakfastPolicy = Constants.pmsIntegrationSettings.breakfastPolicy;
  lunchPolicy = Constants.pmsIntegrationSettings.lunchPolicy;
  dinnerPolicy = Constants.pmsIntegrationSettings.dinnerPolicy;

  mealLabelMap: any[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private reservationService: ReservationService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.clientId = this.route.snapshot.paramMap.get('clientId');

    for (let hour = 0; hour < 24; hour++) {
      for (let minute of [0, 30]) {
        const h = hour.toString().padStart(2, '0');
        const m = minute.toString().padStart(2, '0');
        const time = `${h}:${m}`;
        this.mealTimeOptions.push({ label: time, value: time });
      }
    }

    this.buildHotelSystemFields();
    this.loadInitialData();
    this.getPMSProductData();
    this.getPMSMealSettings();
  }

  private buildHotelSystemFields() {
    // Default validators
    const defaultValidators = [Validators.required];

    // Build dynamic fields from HOTEL_SYSTEM_CONFIG
    Object.keys(HOTEL_SYSTEM_CONFIG).forEach(system => {
      this.HOTEL_SYSTEM_FIELDS[system] = HOTEL_SYSTEM_CONFIG[system].map(field => ({
        ...field,
        type: 'text', // override type to text
        validators: field.validators ? field.validators : defaultValidators
      }));
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadInitialData() {
    this.loading = true;
    this.reservationService
      .getPMSData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: any[]) => {
          this.integrationData = data;
          this.processIntegrationData();
          this.loading = false;
        },
        error => {
          this.loading = false;
          this.snackBar.open(this.translate.instant('PMSIntegration.SomethingWentWrong'), 'Close', { duration: 2000 });
        }
      );
  }

  getPMSProductData() {
    this.loading = true;
    this.mealLabelMap = this.mealOptions.reduce((acc: any, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});

    this.reservationService
      .getPMSProductData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp: any) => {
          if (resp.success) {
            this.productIntegrationData = resp['data'];
          } else {
            this.productIntegrationData = [];
          }
          this.loading = false;
        },
        error => {
          this.loading = false;
          this.snackBar.open(this.translate.instant('PMSProductIntegration.SomethingWentWrong'), 'Close', {
            duration: 2000
          });
        }
      );
  }

  getPMSMealSettings() {
    this.loading = true;
    this.reservationService
      .getPMSMealSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp: any) => {
          if (resp.success) {
            const hasArray = (resp as any).data && Array.isArray((resp as any).data) && (resp as any).data.length > 0;
            const d: any = hasArray ? (resp as any).data[0] : (resp as any);
            if (d.createdAt) {
              this.breakfastPolicy = d.breakfastServedAt;
              this.lunchPolicy = d.lunchServedAt;
              this.dinnerPolicy = d.dinnerServedAt;
            }
            this.dateBasedFilter = d && d.filter ? d.filter : Constants.pmsIntegrationSettings.filter;
            this.stayTimeMinutes = d && d.stayTime ? d.stayTime : Constants.pmsIntegrationSettings.stayTime;
            this.breakfastTime =
              d && d.breakfastTime ? d.breakfastTime : Constants.pmsIntegrationSettings.breakfastTime;
            this.lunchTime = d && d.lunchTime ? d.lunchTime : Constants.pmsIntegrationSettings.lunchTime;
            this.dinnerTime = d && d.dinnerTime ? d.dinnerTime : Constants.pmsIntegrationSettings.dinnerTime;
          }
        },
        error => {
          this.snackBar.open(this.translate.instant('PMSProductIntegration.SomethingWentWrong'), 'Close', {
            duration: 2000
          });
        }
      );
  }

  processIntegrationData() {
    const groupedIntegrations = this.integrationData.reduce((acc, item) => {
      const sys = item.hotelSystem; // Expected to match exactly as in HOTEL_SYSTEM_CONFIG
      if (!acc[sys]) {
        acc[sys] = [];
      }
      acc[sys].push(item);
      return acc;
    }, {} as Record<string, any[]>);

    this.systemIntegrations = Object.entries(groupedIntegrations).map(([system, data]) => ({
      system,
      data: data as any[]
    }));

    // Auto-select if exactly one system has data.
    if (this.systemIntegrations.length === 1) {
      const singleSystem = this.systemIntegrations[0].system;
      this.dropdownSelected = singleSystem;
      this.selectHotelSystem(singleSystem);
    } else {
      this.dropdownSelected = null;
      this.selectedHotelSystem = '';
      this.dynamicForm = null;
    }
  }

  onDropdownChange(event: any) {
    if (!event || !event.value) {
      this.selectedHotelSystem = '';
      this.dynamicForm = null;
      return;
    }
    const systemName = event.value;
    this.selectHotelSystem(systemName);
  }

  selectHotelSystem(system: string) {
    if (!HOTEL_SYSTEM_CONFIG[system]) {
      this.snackBar.open(this.translate.instant('PMSIntegration.SomethingWentWrong'), 'Close', { duration: 2000 });
      return;
    }
    this.selectedHotelSystem = system;
    this.dropdownSelected = system;
    this.createDynamicForm(system);
  }

  createDynamicForm(system: string) {
    const formConfig: any = {};
    const fields = this.HOTEL_SYSTEM_FIELDS[system] || [];
    fields.forEach((field: any) => {
      const match = this.integrationData.find(item => item.hotelSystem === system && item.key === field.key);
      formConfig[field.key] = [match ? match.value : '', field.validators];
    });
    this.dynamicForm = this.fb.group(formConfig);
  }

  submitData() {
    if (!this.selectedHotelSystem) {
      this.snackBar.open(this.translate.instant('PMSIntegration.SelectHotelIntegrations'), 'Close', { duration: 2000 });
      return;
    }
    if (!this.dynamicForm || this.dynamicForm.invalid) {
      this.snackBar.open(this.translate.instant('PMSIntegration.SomethingWentWrong'), 'Close', { duration: 2000 });
      return;
    }
    const formData = this.dynamicForm.value;
    const resultArray = Object.entries(formData).map(([key, value]) => {
      const existing = this.integrationData.find(
        item => item.hotelSystem === this.selectedHotelSystem && item.key === key
      );
      return {
        id: existing ? existing.id : null,
        hotelSystem: this.selectedHotelSystem,
        key,
        clientId: this.clientId,
        value
      };
    });
    this.loading = true;
    this.reservationService
      .createPMSData({
        data: resultArray,
        clientId: this.clientId,
        type: this.selectedHotelSystem
      })
      .subscribe(
        () => {
          this.loading = false;
          this.snackBar.open(this.translate.instant('PMSIntegration.Success'), 'Close', { duration: 2000 });
          this.loadInitialData();
        },
        error => {
          this.loading = false;
          this.snackBar.open(this.translate.instant('PMSIntegration.SomethingWentWrong'), 'Close', { duration: 2000 });
          console.error(error);
        }
      );
  }

  hasExistingData(): boolean {
    return this.integrationData.some(item => item.hotelSystem === this.selectedHotelSystem);
  }

  deleteData() {
    if (!this.hasExistingData()) {
      return;
    }
    const modalRef = this.modalService.open(ConfirmModalComponent);
    modalRef.componentInstance.title = this.translate.instant('PMSIntegration.DeleteIntegration');
    modalRef.componentInstance.message = this.translate.instant('PMSIntegration.AreYouSureDelete'); // Create key accordingly
    modalRef.componentInstance.showInfo = false;
    modalRef.componentInstance.buttonText = this.translate.instant('PMSIntegration.Yes');

    modalRef.result.then(
      result => {
        if (result === 'ok') {
          this.loading = true;
          this.reservationService
            .deletPMSData({
              clientId: this.clientId,
              type: this.selectedHotelSystem
            })
            .subscribe(() => {
              this.loading = false;
              this.loadInitialData();
              if (this.dynamicForm) {
                this.dynamicForm.reset();
              }
              this.snackBar.open(this.translate.instant('PMSIntegration.Success'), 'Close', { duration: 2000 });
            });
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  onProductFormSubmit() {
    const modalRef = this.modalService.open(PmsProductModalComponent);
    modalRef.componentInstance.title = this.translate.instant('PMSProductIntegration.AddProduct');
    modalRef.componentInstance.showInfo = false;
    modalRef.componentInstance.buttonText = this.translate.instant('PMSProductIntegration.Add');

    modalRef.result.then(
      (result: any) => {
        console.log(result);
        if (result && result.success) {
          this.loading = true;
          const {
            data: { ratePlanCodeName, mealName }
          } = result;
          this.reservationService
            .createPMSProductData({
              ratePlanCodeName,
              mealName,
              clientId: +this.clientId
            })
            .subscribe(
              () => {
                this.loading = false;
                this.snackBar.open(this.translate.instant('PMSProductIntegration.SaveIntegration'), 'Close', {
                  duration: 2000
                });
                this.getPMSProductData();
              },
              error => {
                this.loading = false;
                console.log(error);
                this.snackBar.open(
                  error.error.msg || this.translate.instant('PMSProductIntegration.SomethingWentWrong'),
                  'Close',
                  { duration: 2000 }
                );
                console.error(error);
              }
            );
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  editPMSProductData(data: any) {
    const modalRef = this.modalService.open(PmsProductModalComponent);
    modalRef.componentInstance.title = this.translate.instant('PMSProductIntegration.UpdateHotelIntegrations');
    modalRef.componentInstance.ratePlanCodeName = data.ratePlanCodeName;
    modalRef.componentInstance.mealName = data.mealName;
    modalRef.componentInstance.showInfo = false;
    modalRef.componentInstance.buttonText = this.translate.instant('PMSProductIntegration.Update');

    modalRef.result.then(
      (result: any) => {
        if (result && result.success) {
          this.loading = true;
          const {
            data: { ratePlanCodeName, mealName }
          } = result;
          this.reservationService
            .createPMSProductData({
              ratePlanCodeName: ratePlanCodeName,
              mealName: mealName,
              clientId: +this.clientId,
              id: data.id
            })
            .subscribe(
              () => {
                this.loading = false;
                this.snackBar.open(this.translate.instant('PMSProductIntegration.UpdateHotelIntegrations'), 'Close', {
                  duration: 2000
                });
                this.getPMSProductData();
              },
              error => {
                this.loading = false;
                this.snackBar.open(this.translate.instant('PMSProductIntegration.SomethingWentWrong'), 'Close', {
                  duration: 2000
                });
                console.error(error);
              }
            );
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  deletePMSProductData(id: number) {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    modalRef.componentInstance.title = this.translate.instant('PMSProductIntegration.DeleteIntegration');
    modalRef.componentInstance.message = this.translate.instant('PMSProductIntegration.AreYouSureDelete'); // Create key accordingly
    modalRef.componentInstance.showInfo = false;
    modalRef.componentInstance.buttonText = this.translate.instant('PMSProductIntegration.Yes');

    modalRef.result.then(
      result => {
        if (result === 'ok') {
          this.loading = true;
          this.reservationService
            .deletPMSProductData({
              id
            })
            .subscribe((resp: any) => {
              console.log(resp);
              this.loading = false;
              if (resp.success) {
                this.getPMSProductData();
                this.snackBar.open(this.translate.instant('PMSProductIntegration.Success'), 'Close', {
                  duration: 2000
                });
              } else {
                this.snackBar.open(this.translate.instant('PMSProductIntegration.SomethingWentWrong'), 'Close', {
                  duration: 2000
                });
              }
            });
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  getBackgroundColor(mealName: any) {
    return this.mealOptions.find(x => x.value === mealName).color || '';
  }

  onMealPolicyChange(mealType: 'breakfast' | 'lunch' | 'dinner') {
    let policyValue;
    if (mealType === 'breakfast') policyValue = this.breakfastPolicy;
    if (mealType === 'lunch') policyValue = this.lunchPolicy;
    if (mealType === 'dinner') policyValue = this.dinnerPolicy;
    this.reservationService.createPMSMealSettings({ mealType, policyValue, clientId: +this.clientId }).subscribe();
  }
  onStayTimeChange(val: number) {
    this.reservationService
      .createPMSMealSettings({
        clientId: +this.clientId,
        stayTimeMinutes: val
      })
      .subscribe();
  }

  onDateBasedFilterChange(val: number) {
    this.reservationService
      .createPMSMealSettings({
        clientId: +this.clientId,
        dateFilter: val
      })
      .subscribe();
  }

  onMealTimeChange(which: 'breakfast' | 'lunch' | 'dinner') {
    let mealTimeValue = '';
    if (which === 'breakfast') mealTimeValue = this.breakfastTime;
    if (which === 'lunch') mealTimeValue = this.lunchTime;
    if (which === 'dinner') mealTimeValue = this.dinnerTime;

    // Send to the same endpoint
    this.reservationService
      .createPMSMealSettings({
        clientId: +this.clientId,
        mealType: which,
        mealTimeValue
      })
      .subscribe();
  }
}
